import { create } from 'zustand';

interface SuccessFeeLockState {
  /** When the API last refused a call over the terms; null once released. */
  lockedAt: number | null;
  /** ISO instant the dashboard closed, read off the refusal body. */
  deadline: string | null;
  lock: (deadline: string | null) => void;
  release: () => void;
}

/**
 * The lock as the browser learned it, from a refused request.
 *
 * The terms endpoint is the authority and stays readable while locked, but it
 * is only re-read every few minutes. A session open when the deadline passes
 * would otherwise spend that gap watching every panel fail. Any refusal flips
 * this at once; the timestamp is what lets a later answer from the terms
 * endpoint overrule it, rather than a stale flag holding a merchant who already
 * finished the move.
 *
 * Deliberately not persisted, for the same reason.
 */
export const useSuccessFeeLockStore = create<SuccessFeeLockState>()((set) => ({
  lockedAt: null,
  deadline: null,

  /**
   * Idempotent while already locked, on purpose. A locked dashboard refuses a
   * burst of calls, and moving the timestamp on each one would keep restarting
   * the re-read that is meant to settle the question, against an API answering
   * refusals.
   */
  lock: (deadline) =>
    set((state) =>
      state.lockedAt === null
        ? { lockedAt: Date.now(), deadline }
        : { deadline: state.deadline ?? deadline }
    ),

  release: () => set({ lockedAt: null, deadline: null }),
}));
